import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import katex from "katex";
import "katex/dist/katex.min.css";
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomToolbar from './CustomToolbar';

window.katex = katex;

const Editor = forwardRef(({ value, onChange, readOnly = false, onReady, isBlog = false }, ref) => {
  const quillRef = useRef(null);
  const iframeRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  useImperativeHandle(ref, () => ({
    showPreview: handlePreview,
  }));

  const modules = {
    toolbar: { container: "#toolbar" }
  };

  const formats = [
    'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'header', 'blockquote', 'code-block',
    'indent', 'list',
    'direction', 'align',
    'link', 'image', 'video', 'formula'
  ];

   const placeholder = isBlog
    ? "Write your Blog content here..."
    : "Write your Custom Email content here...";

  //  Expose the real Quill instance to parent
  useEffect(() => {
    if (quillRef.current && onReady) {
      onReady(quillRef.current.getEditor());
    }
  }, [onReady]);

  const handlePreview = () => {
    const raw = value || '';

    if (!raw || !/<!DOCTYPE|<html|<head|<body/i.test(raw)) {
      setPreviewHtml(raw);
      setPreviewOpen(true);
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(raw, 'text/html');

      doc.querySelectorAll('script, noscript').forEach((el) => el.remove());

      const serializeAttrs = (el) => {
        if (!el) return '';
        const names = el.getAttributeNames();
        if (names.length === 0) return '';
        return ' ' + names.map((n) => `${n}="${el.getAttribute(n).replace(/"/g, '&quot;')}"`).join(' ');
      };

      const htmlAttrs = serializeAttrs(doc.documentElement);
      const headContent = doc.head ? doc.head.innerHTML : '';
      const bodyAttrs = serializeAttrs(doc.body);
      const bodyContent = doc.body ? doc.body.innerHTML : raw;

      setPreviewHtml(`<!DOCTYPE html><html${htmlAttrs}><head>${headContent}</head><body${bodyAttrs}>${bodyContent}</body></html>`);
      setPreviewOpen(true);
    } catch (e) {
      console.warn('Preview parsing failed:', e);
      setPreviewHtml(raw);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewHtml('');
  };

  return (
    <Box>
      <CustomToolbar />

      <ReactQuill
        ref={quillRef}
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        style={{ height: '150px' }}
        placeholder={placeholder}
        readOnly={readOnly}
      />

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxWidth: '1200px' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" fontWeight={600}>Preview</Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, flex: 1, display: 'flex' }}>
          <iframe
            ref={iframeRef}
            title="HTML Preview"
            srcDoc={previewHtml}
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-same-origin"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default Editor;
